import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Clock, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useEventTiming } from './EventTimingContext';
import { SortableTimingRow } from './SortableTimingRow';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { WARMUP_ADD_OPTIONS, TIME_PER_SKATER_OPTIONS } from './constants';

export default function FreeskatingTab() {
  const {
    isSuperUser,
    schedules,
    dialogOpen,
    setDialogOpen,
    editingTiming,
    setEditingTiming,
    saving,
    setSaving,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete,
    deleting,
    handleDeleteClick,
    handleConfirmDelete,
    selectedScheduleId,
    setSelectedScheduleId,
    minutesPostWarmup,
    setMinutesPostWarmup,
    addForPanelSwap,
    setAddForPanelSwap,
    useSoloForCouples,
    setUseSoloForCouples,
    maxSkatersWarmupCadetSenior,
    setMaxSkatersWarmupCadetSenior,
    maxSkatersWarmupOther,
    setMaxSkatersWarmupOther,
    timingsLists,
    fetchTimings,
    sensors,
    handleDragEnd,
    canEditTiming,
    resetForm,
    calculateWarmupTotal,
    formatTimeOnBlur,
  } = useEventTiming();

  const [freeskatingPage, setFreeskatingPage] = useState(1);
  const [freeskatingGrades, setFreeskatingGrades] = useState([]);
  const [freeskatingInlineGrades, setFreeskatingInlineGrades] = useState([]);
  const [freeskatingTimings, setFreeskatingTimings] = useState({});
  const [freeskatingInlineTimings, setFreeskatingInlineTimings] = useState({});

  const timingsList = timingsLists.freeskating || [];

  // Fetch Freeskating grades when schedule is selected
  useEffect(() => {
    if (!selectedScheduleId || editingTiming) return;

    const fetchFreeskatingGrades = async () => {
      try {
        const [fsRes, fsiRes] = await Promise.all([
          axios.get(`${API}/event-timings/freeskating-grades/${selectedScheduleId}`, { withCredentials: true }),
          axios.get(`${API}/event-timings/freeskating-inline-grades/${selectedScheduleId}`, { withCredentials: true })
        ]);
        
        const fsGrades = fsRes.data || [];
        const fsiGrades = fsiRes.data || [];
        
        setFreeskatingGrades(fsGrades);
        setFreeskatingInlineGrades(fsiGrades);
        
        const fsTimings = {};
        fsGrades.forEach(g => {
          fsTimings[g.grade_id] = {
            programme_time: g.programme_time || '',
            add_for_warmup: '2:00',
            time_per_skater: '6:00',
            can_shorten: false
          };
        });
        setFreeskatingTimings(fsTimings);
        
        const fsiTimings = {};
        fsiGrades.forEach(g => {
          fsiTimings[g.grade_id] = {
            programme_time: g.programme_time || '',
            add_for_warmup: '2:00',
            time_per_skater: '6:00',
            can_shorten: false
          };
        });
        setFreeskatingInlineTimings(fsiTimings);
      } catch (error) {
        console.error('Error fetching freeskating grades:', error);
        toast.error('Failed to load grades for this schedule');
      }
    };
    fetchFreeskatingGrades();
  }, [selectedScheduleId, editingTiming]);

  const localResetForm = () => {
    resetForm();
    setFreeskatingPage(1);
    setFreeskatingGrades([]);
    setFreeskatingInlineGrades([]);
    setFreeskatingTimings({});
    setFreeskatingInlineTimings({});
  };

  const handleAddTimings = () => {
    localResetForm();
    setDialogOpen(true);
  };

  const handleEditTiming = async (timing) => {
    setEditingTiming(timing);
    setSelectedScheduleId(timing.schedule_id);
    setMinutesPostWarmup(timing.minutes_post_warmup_first_skater || '1:00');
    setAddForPanelSwap(timing.add_for_panel_swap || '1:00');
    setUseSoloForCouples(timing.use_solo_for_couples ?? true);
    setMaxSkatersWarmupCadetSenior(timing.max_skaters_warmup_cadet_senior ?? 5);
    setMaxSkatersWarmupOther(timing.max_skaters_warmup_other ?? 6);
    setFreeskatingPage(1);
    
    try {
      const [fsRes, fsiRes] = await Promise.all([
        axios.get(`${API}/event-timings/freeskating-grades/${timing.schedule_id}`, { withCredentials: true }),
        axios.get(`${API}/event-timings/freeskating-inline-grades/${timing.schedule_id}`, { withCredentials: true })
      ]);
      
      const fsGrades = fsRes.data || [];
      const fsiGrades = fsiRes.data || [];
      
      setFreeskatingGrades(fsGrades);
      setFreeskatingInlineGrades(fsiGrades);
      
      const savedTimingsMap = {};
      (timing.grade_timings || []).forEach(gt => {
        savedTimingsMap[gt.grade_id] = gt;
      });
      
      const fsTimings = {};
      fsGrades.forEach(g => {
        const saved = savedTimingsMap[g.grade_id];
        fsTimings[g.grade_id] = {
          programme_time: g.programme_time || '',
          add_for_warmup: saved?.add_for_warmup || '2:00',
          time_per_skater: saved?.time_per_skater || '6:00',
          can_shorten: saved?.can_shorten || false
        };
      });
      setFreeskatingTimings(fsTimings);
      
      const fsiTimings = {};
      fsiGrades.forEach(g => {
        const saved = savedTimingsMap[g.grade_id];
        fsiTimings[g.grade_id] = {
          programme_time: g.programme_time || '',
          add_for_warmup: saved?.add_for_warmup || '2:00',
          time_per_skater: saved?.time_per_skater || '6:00',
          can_shorten: saved?.can_shorten || false
        };
      });
      setFreeskatingInlineTimings(fsiTimings);
    } catch (error) {
      console.error('Error fetching grades for edit:', error);
      toast.error('Failed to load timing data');
      return;
    }
    
    setDialogOpen(true);
  };

  const updateGradeTiming = (gradeId, field, value, isInline = false) => {
    if (isInline) {
      setFreeskatingInlineTimings(prev => ({
        ...prev,
        [gradeId]: { ...prev[gradeId], [field]: value }
      }));
    } else {
      setFreeskatingTimings(prev => ({
        ...prev,
        [gradeId]: { ...prev[gradeId], [field]: value }
      }));
    }
  };

  const handleSave = async (andNext = false) => {
    if (!selectedScheduleId) {
      toast.error('Please select a schedule');
      return;
    }

    setSaving(true);
    try {
      const allGradeTimings = [
        ...Object.entries(freeskatingTimings).map(([gradeId, timing]) => ({
          grade_id: gradeId,
          discipline: 'Freeskating',
          ...timing
        })),
        ...Object.entries(freeskatingInlineTimings).map(([gradeId, timing]) => ({
          grade_id: gradeId,
          discipline: 'Freeskating Inline',
          ...timing
        }))
      ];

      const payload = {
        schedule_id: selectedScheduleId,
        event_type: 'freeskating',
        grade_timings: allGradeTimings,
        minutes_post_warmup_first_skater: minutesPostWarmup,
        add_for_panel_swap: addForPanelSwap,
        use_solo_for_couples: useSoloForCouples,
        max_skaters_warmup_cadet_senior: maxSkatersWarmupCadetSenior,
        max_skaters_warmup_other: maxSkatersWarmupOther
      };

      await axios.post(`${API}/event-timings`, payload, { withCredentials: true });
      
      if (andNext) {
        toast.success(editingTiming ? 'Freeskating timings updated' : 'Freeskating timings saved');
        setFreeskatingPage(2);
      } else {
        toast.success(editingTiming ? 'Freeskating timings updated successfully' : 'Freeskating timings saved successfully');
        setDialogOpen(false);
        localResetForm();
        fetchTimings('freeskating');
      }
    } catch (error) {
      console.error('Error saving timings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save timings');
    } finally {
      setSaving(false);
    }
  };

  const renderGradesTable = (gradesList, timingsObj, isInline = false) => (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-green-100">
          <th className="border px-3 py-2 text-left w-48">Grade</th>
          <th className="border px-3 py-2 text-center w-36">Programme Time</th>
          <th className="border px-3 py-2 text-center w-28">Add for Warmup</th>
          <th className="border px-3 py-2 text-center w-24">Warm Up</th>
          <th className="border px-3 py-2 text-center w-28">Time per Skater</th>
          <th className="border px-3 py-2 text-center w-24">Can Shorten</th>
        </tr>
      </thead>
      <tbody>
        {gradesList.map((grade, index) => {
          const timing = timingsObj[grade.grade_id] || {};
          const warmupTotal = calculateWarmupTotal(timing.programme_time, timing.add_for_warmup);
          
          const showHR = index > 0 && 
            gradesList[index - 1].classification_section !== grade.classification_section;
          
          return (
            <React.Fragment key={grade.grade_id}>
              {showHR && (
                <tr>
                  <td colSpan={6} style={{ border: 'none', padding: '16px 0', background: 'white' }}>
                    <div style={{ height: '2px', backgroundColor: '#a8a29e' }}></div>
                  </td>
                </tr>
              )}
              <tr>
                <td className="border px-3 py-2 bg-white font-medium">{grade.name}</td>
                <td className="border px-3 py-2 bg-white text-center text-sm">
                  {timing.programme_time || '-'}
                </td>
                <td className="border px-1 py-1 bg-white">
                  <Select 
                    value={timing.add_for_warmup || '2:00'} 
                    onValueChange={(v) => updateGradeTiming(grade.grade_id, 'add_for_warmup', v, isInline)}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                      {WARMUP_ADD_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="border px-3 py-2 bg-gray-50 text-center font-medium">
                  {warmupTotal || '-'}
                </td>
                <td className="border px-1 py-1 bg-white">
                  <Select 
                    value={timing.time_per_skater || '6:00'} 
                    onValueChange={(v) => updateGradeTiming(grade.grade_id, 'time_per_skater', v, isInline)}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                      {TIME_PER_SKATER_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="border px-3 py-2 bg-white text-center">
                  <Checkbox
                    checked={timing.can_shorten || false}
                    onCheckedChange={(checked) => updateGradeTiming(grade.grade_id, 'can_shorten', checked, isInline)}
                  />
                </td>
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Freeskating & Freeskating Inline</h2>
        <Button onClick={handleAddTimings} className="btn-primary" data-testid="add-timings-freeskating">
          <Plus className="mr-2 h-4 w-4" />
          Add Timings
        </Button>
      </div>
      
      {!isSuperUser && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          You can create and edit timing tables for schedules you own. Timing tables for system schedules are view-only.
        </div>
      )}
      
      <Card className="p-6">
        {timingsList.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 text-stone-300" />
            <p>No timing entries yet for Freeskating.</p>
            <p className="text-sm mt-1">Click "Add Timings" to create your first entry.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'freeskating')}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="w-8"></th>
                    <th className="text-left py-3 px-2 font-semibold">Schedule</th>
                    <th className="text-center py-3 px-2 font-semibold">Grades Configured</th>
                    <th className="text-center py-3 px-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <SortableContext items={timingsList.map(t => t.timing_id)} strategy={verticalListSortingStrategy}>
                    {timingsList.map(timing => (
                      <SortableTimingRow
                        key={timing.timing_id}
                        id={timing.timing_id}
                        timing={timing}
                        canEdit={canEditTiming(timing)}
                        canReorder={isSuperUser}
                        onEdit={handleEditTiming}
                        onDelete={handleDeleteClick}
                      />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </div>
          </DndContext>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(val) => { setDialogOpen(val); if (!val) localResetForm(); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTiming ? 'Edit' : 'Add'} {freeskatingPage === 1 ? 'Freeskating' : 'Freeskating Inline'} Timings
              <span className="text-sm font-normal text-muted-foreground ml-2">
                (Page {freeskatingPage} of 2)
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div>
              <Label>Select Schedule *</Label>
              <Select 
                value={selectedScheduleId} 
                onValueChange={setSelectedScheduleId}
                disabled={!!editingTiming || freeskatingPage === 2}
              >
                <SelectTrigger data-testid="schedule-select">
                  <SelectValue placeholder="Choose a schedule..." />
                </SelectTrigger>
                <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                  {schedules.map(s => (
                    <SelectItem key={s.schedule_id} value={s.schedule_id}>
                      {s.schedule_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(editingTiming || freeskatingPage === 2) && (
                <p className="text-xs text-muted-foreground mt-1">Schedule cannot be changed</p>
              )}
            </div>

            {freeskatingPage === 1 && freeskatingGrades.length > 0 && (
              <div className="overflow-x-auto">
                <h3 className="font-medium mb-2">Freeskating Grades</h3>
                {renderGradesTable(freeskatingGrades, freeskatingTimings, false)}
              </div>
            )}

            {freeskatingPage === 2 && freeskatingInlineGrades.length > 0 && (
              <div className="overflow-x-auto">
                <h3 className="font-medium mb-2">Freeskating Inline Grades</h3>
                {renderGradesTable(freeskatingInlineGrades, freeskatingInlineTimings, true)}
              </div>
            )}

            {freeskatingPage === 2 && freeskatingInlineGrades.length === 0 && selectedScheduleId && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No Freeskating Inline grades found for this schedule.</p>
              </div>
            )}

            {((freeskatingPage === 1 && freeskatingGrades.length > 0) || 
              (freeskatingPage === 2 && freeskatingInlineGrades.length >= 0)) && selectedScheduleId && (
              <div className="space-y-4 pt-4 border-t">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Minutes post warm up - first skater</Label>
                    <Input
                      value={minutesPostWarmup}
                      onChange={(e) => setMinutesPostWarmup(e.target.value)}
                      onBlur={() => formatTimeOnBlur(minutesPostWarmup, setMinutesPostWarmup)}
                      placeholder="1:00"
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Format: m:ss (e.g., 1:30)</p>
                  </div>
                  
                  <div>
                    <Label>Add for panel swap - men in group</Label>
                    <Input
                      value={addForPanelSwap}
                      onChange={(e) => setAddForPanelSwap(e.target.value)}
                      onBlur={() => formatTimeOnBlur(addForPanelSwap, setAddForPanelSwap)}
                      placeholder="1:00"
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Format: m:ss (e.g., 1:00)</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">Max skaters per warm up group</Label>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div>
                        <Label className="text-sm text-muted-foreground">Cadet-Senior & Pairs</Label>
                        <Select 
                          value={String(maxSkatersWarmupCadetSenior)} 
                          onValueChange={(v) => setMaxSkatersWarmupCadetSenior(parseInt(v))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                            {[5, 6, 7, 8, 9, 10].map(n => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Other Skaters</Label>
                        <Select 
                          value={String(maxSkatersWarmupOther)} 
                          onValueChange={(v) => setMaxSkatersWarmupOther(parseInt(v))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                            {[5, 6, 7, 8, 9, 10].map(n => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Use solo timings for same grade couples event?</Label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="useSoloForCouplesFS"
                          checked={useSoloForCouples === true}
                          onChange={() => setUseSoloForCouples(true)}
                        />
                        <span>Yes</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="useSoloForCouplesFS"
                          checked={useSoloForCouples === false}
                          onChange={() => setUseSoloForCouples(false)}
                        />
                        <span>No</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between gap-3 pt-4 border-t">
              <div>
                {freeskatingPage === 2 && (
                  <Button variant="outline" onClick={() => setFreeskatingPage(1)}>
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Back to Freeskating
                  </Button>
                )}
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                
                {freeskatingPage === 1 && (
                  <>
                    <Button 
                      variant="outline"
                      onClick={() => handleSave(true)}
                      disabled={!selectedScheduleId || freeskatingGrades.length === 0 || saving}
                    >
                      Save & Next
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                    <Button 
                      onClick={() => handleSave(false)} 
                      disabled={!selectedScheduleId || freeskatingGrades.length === 0 || saving}
                      className="btn-primary"
                    >
                      {saving ? 'Saving...' : (editingTiming ? 'Update All' : 'Save All')}
                    </Button>
                  </>
                )}
                
                {freeskatingPage === 2 && (
                  <Button 
                    onClick={() => handleSave(false)} 
                    disabled={saving}
                    className="btn-primary"
                  >
                    {saving ? 'Saving...' : (editingTiming ? 'Update All' : 'Save All')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        itemName={itemToDelete?.schedule_name ? `timing for "${itemToDelete.schedule_name}"` : 'this timing'}
        loading={deleting}
      />
    </>
  );
}
